import React, { useState } from "react";
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  Paper,
  Tabs,
  Tab,
  useTheme,
  useMediaQuery,
  CircularProgress,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Tooltip,
  Badge,
} from "@mui/material";
import {
  ViewModule,
  TrendingUp,
  AutoFixHigh,
  PlayArrow,
  Settings,
  Add,
  Edit,
  Delete,
  ContentCopy,
  PlayArrow as PlayIcon,
  Stop,
  Visibility,
  MoreVert,
} from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import {
  useScenarios,
  useScenarioStats,
  useActiveScenarios,
} from "../../hooks/useScenarios";
import {
  Scenario,
  ScenarioCategory,
  RiskLevel,
  TimeFrame,
} from "../../types/scenario";

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`scenarios-tabpanel-${index}`}
      aria-labelledby={`scenarios-tab-${index}`}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

function a11yProps(index: number) {
  return {
    id: `scenarios-tab-${index}`,
    "aria-controls": `scenarios-tabpanel-${index}`,
  };
}

export function ScenariosPage() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const [tabValue, setTabValue] = useState(0);

  // Fetch real data using custom hooks
  const {
    scenarios,
    loading: scenariosLoading,
    error: scenariosError,
  } = useScenarios();
  const {
    stats,
    loading: statsLoading,
    error: statsError,
  } = useScenarioStats();
  const {
    scenarios: activeScenarios,
    loading: activeLoading,
    error: activeError,
  } = useActiveScenarios();

  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const handleCreateScenario = () => {
    navigate("/scenarios/create");
  };

  const handleViewPerformance = () => {
    navigate("/scenarios/performance");
  };

  const handleEditScenario = (scenarioId: string) => {
    navigate(`/scenarios/edit/${scenarioId}`);
  };

  const handleViewScenario = (scenarioId: string) => {
    navigate(`/scenarios/${scenarioId}`);
  };

  const getCategoryColor = (category: ScenarioCategory) => {
    const colors: Record<ScenarioCategory, string> = {
      [ScenarioCategory.DAY_TRADING]: "primary",
      [ScenarioCategory.SWING_TRADING]: "secondary",
      [ScenarioCategory.POSITION_TRADING]: "success",
      [ScenarioCategory.SCALPING]: "warning",
      [ScenarioCategory.ARBITRAGE]: "info",
      [ScenarioCategory.MEAN_REVERSION]: "error",
      [ScenarioCategory.MOMENTUM]: "primary",
      [ScenarioCategory.BREAKOUT]: "secondary",
      [ScenarioCategory.REVERSAL]: "success",
      [ScenarioCategory.CUSTOM]: "default",
    };
    return colors[category] || "default";
  };

  const getRiskLevelColor = (riskLevel: RiskLevel) => {
    const colors: Record<RiskLevel, string> = {
      [RiskLevel.CONSERVATIVE]: "success",
      [RiskLevel.MODERATE]: "info",
      [RiskLevel.AGGRESSIVE]: "warning",
      [RiskLevel.EXTREME]: "error",
    };
    return colors[riskLevel] || "default";
  };

  const getTimeFrameColor = (timeFrame: TimeFrame) => {
    const colors: Record<TimeFrame, string> = {
      [TimeFrame.INTRADAY]: "primary",
      [TimeFrame.OVERNIGHT]: "secondary",
      [TimeFrame.WEEKLY]: "success",
      [TimeFrame.MONTHLY]: "info",
      [TimeFrame.QUARTERLY]: "warning",
      [TimeFrame.YEARLY]: "error",
    };
    return colors[timeFrame] || "default";
  };

  if (statsLoading || scenariosLoading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        minHeight="400px"
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Container maxWidth="xl" sx={{ py: 3 }}>
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" component="h1" gutterBottom>
          Trading Scenarios
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Manage your trading strategies, templates, and performance tracking
        </Typography>
      </Box>

      {/* Statistics Cards */}
      {stats && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Total Scenarios
                </Typography>
                <Typography variant="h4" component="div">
                  {stats.totalScenarios}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Active Scenarios
                </Typography>
                <Typography variant="h4" component="div" color="success.main">
                  {stats.activeScenarios}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Win Rate
                </Typography>
                <Typography variant="h4" component="div" color="primary.main">
                  {stats.averageWinRate?.toFixed(1) || 0}%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Typography color="text.secondary" gutterBottom>
                  Net Profit
                </Typography>
                <Typography
                  variant="h4"
                  component="div"
                  color={stats.netProfit >= 0 ? "success.main" : "error.main"}
                >
                  ${stats.netProfit?.toLocaleString() || 0}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      {/* Quick Actions */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              cursor: "pointer",
              "&:hover": {
                transform: "translateY(-2px)",
                transition: "transform 0.2s",
              },
            }}
            onClick={handleCreateScenario}
          >
            <CardContent sx={{ textAlign: "center" }}>
              <Add sx={{ fontSize: 40, color: "primary.main", mb: 1 }} />
              <Typography variant="h6" component="div">
                Create Scenario
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Build a new trading strategy
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              cursor: "pointer",
              "&:hover": {
                transform: "translateY(-2px)",
                transition: "transform 0.2s",
              },
            }}
            onClick={() => navigate("/scenarios/templates")}
          >
            <CardContent sx={{ textAlign: "center" }}>
              <ViewModule
                sx={{ fontSize: 40, color: "secondary.main", mb: 1 }}
              />
              <Typography variant="h6" component="div">
                Templates
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Use pre-built strategies
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              cursor: "pointer",
              "&:hover": {
                transform: "translateY(-2px)",
                transition: "transform 0.2s",
              },
            }}
            onClick={handleViewPerformance}
          >
            <CardContent sx={{ textAlign: "center" }}>
              <TrendingUp sx={{ fontSize: 40, color: "success.main", mb: 1 }} />
              <Typography variant="h6" component="div">
                Performance
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Track your results
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <Card
            sx={{
              cursor: "pointer",
              "&:hover": {
                transform: "translateY(-2px)",
                transition: "transform 0.2s",
              },
            }}
            onClick={() => navigate("/scenarios/automation")}
          >
            <CardContent sx={{ textAlign: "center" }}>
              <AutoFixHigh
                sx={{ fontSize: 40, color: "warning.main", mb: 1 }}
              />
              <Typography variant="h6" component="div">
                Automation
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Set up auto-trading
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Error Display */}
      {(scenariosError || statsError || activeError) && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {scenariosError || statsError || activeError}
        </Alert>
      )}

      {/* Main Content Tabs */}
      <Paper sx={{ width: "100%" }}>
        <Tabs
          value={tabValue}
          onChange={handleTabChange}
          aria-label="scenarios tabs"
          variant={isMobile ? "scrollable" : "fullWidth"}
          scrollButtons={isMobile ? "auto" : false}
        >
          <Tab label="All Scenarios" {...a11yProps(0)} />
          <Tab label="Active Scenarios" {...a11yProps(1)} />
          <Tab label="Templates" {...a11yProps(2)} />
          <Tab label="Performance" {...a11yProps(3)} />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 2,
            }}
          >
            <Typography variant="h6">All Scenarios</Typography>
            <Button
              variant="contained"
              startIcon={<Add />}
              onClick={handleCreateScenario}
            >
              Create New
            </Button>
          </Box>

          {scenariosLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : !scenarios || scenarios.length === 0 ? (
            <Box textAlign="center" p={3}>
              <Typography variant="body1" color="text.secondary">
                No scenarios found. Create your first trading scenario to get
                started.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Name</TableCell>
                    <TableCell>Category</TableCell>
                    <TableCell>Risk Level</TableCell>
                    <TableCell>Time Frame</TableCell>
                    <TableCell>Status</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {scenarios.map((scenario) => (
                    <TableRow key={scenario.id}>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {scenario.name}
                        </Typography>
                        {scenario.description && (
                          <Typography variant="body2" color="text.secondary">
                            {scenario.description}
                          </Typography>
                        )}
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={scenario.category.replace("_", " ")}
                          color={getCategoryColor(scenario.category) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={scenario.riskLevel}
                          color={getRiskLevelColor(scenario.riskLevel) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={scenario.timeFrame}
                          color={getTimeFrameColor(scenario.timeFrame) as any}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={scenario.isActive ? "Active" : "Inactive"}
                          color={scenario.isActive ? "success" : "default"}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Box sx={{ display: "flex", gap: 1 }}>
                          <Tooltip title="View">
                            <IconButton
                              size="small"
                              onClick={() => handleViewScenario(scenario.id)}
                            >
                              <Visibility />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Edit">
                            <IconButton
                              size="small"
                              onClick={() => handleEditScenario(scenario.id)}
                            >
                              <Edit />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Clone">
                            <IconButton size="small">
                              <ContentCopy />
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="Delete">
                            <IconButton size="small" color="error">
                              <Delete />
                            </IconButton>
                          </Tooltip>
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Active Scenarios
          </Typography>
          {activeLoading ? (
            <Box display="flex" justifyContent="center" p={3}>
              <CircularProgress />
            </Box>
          ) : activeScenarios.length === 0 ? (
            <Box textAlign="center" p={3}>
              <Typography variant="body1" color="text.secondary">
                No active scenarios. Activate a scenario to start automated
                trading.
              </Typography>
            </Box>
          ) : (
            <Grid container spacing={2}>
              {activeScenarios.map((scenario) => (
                <Grid item xs={12} sm={6} md={4} key={scenario.id}>
                  <Card>
                    <CardContent>
                      <Box
                        sx={{
                          display: "flex",
                          justifyContent: "space-between",
                          alignItems: "flex-start",
                          mb: 1,
                        }}
                      >
                        <Typography variant="h6" component="div">
                          {scenario.name}
                        </Typography>
                        <Chip
                          label="Active"
                          color="success"
                          size="small"
                          icon={<PlayIcon />}
                        />
                      </Box>
                      {scenario.description && (
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          sx={{ mb: 2 }}
                        >
                          {scenario.description}
                        </Typography>
                      )}
                      <Box sx={{ display: "flex", gap: 1, mb: 2 }}>
                        <Chip
                          label={scenario.category.replace("_", " ")}
                          color={getCategoryColor(scenario.category) as any}
                          size="small"
                        />
                        <Chip
                          label={scenario.riskLevel}
                          color={getRiskLevelColor(scenario.riskLevel) as any}
                          size="small"
                        />
                      </Box>
                    </CardContent>
                    <CardActions>
                      <Button size="small" startIcon={<Visibility />}>
                        View
                      </Button>
                      <Button size="small" startIcon={<Settings />}>
                        Settings
                      </Button>
                      <Button size="small" startIcon={<Stop />} color="error">
                        Stop
                      </Button>
                    </CardActions>
                  </Card>
                </Grid>
              ))}
            </Grid>
          )}
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Scenario Templates
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Browse and use pre-built trading strategy templates.
          </Typography>
          <Button
            variant="contained"
            startIcon={<Add />}
            sx={{ mt: 2 }}
            onClick={() => navigate("/scenarios/templates")}
          >
            Browse Templates
          </Button>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          <Typography variant="h6" sx={{ mb: 2 }}>
            Performance Overview
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Track and analyze your trading scenario performance.
          </Typography>
          <Button
            variant="contained"
            startIcon={<TrendingUp />}
            sx={{ mt: 2 }}
            onClick={handleViewPerformance}
          >
            View Performance Dashboard
          </Button>
        </TabPanel>
      </Paper>
    </Container>
  );
}
